import { useEffect, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { getAppointmentReview, submitAppointmentReview } from '../../../../services/PublicBookingService';
import PublicBrandSignature from '../../../components/Common/PublicBrandSignature';
import './AppointmentReview.css';

const RATING_LABELS = {
    1: 'Muito ruim',
    2: 'Ruim',
    3: 'Regular',
    4: 'Bom',
    5: 'Excelente'
};

const STARS = [1, 2, 3, 4, 5];
const COMMENT_MAX_LENGTH = 1000;

// Formulário que o cliente abre pelo link do e-mail enviado ao fim do atendimento.
// Sem login: o token da URL identifica a avaliação e nenhuma outra.
const AppointmentReviewPage = () => {
    const { token } = useParams();
    const [review, setReview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [rating, setRating] = useState(0);
    // Estrela sob o cursor: mostra a nota antes de clicar, sem alterar a escolhida.
    const [hoveredRating, setHoveredRating] = useState(0);
    const [comment, setComment] = useState('');

    useEffect(() => {
        getAppointmentReview(token)
            .then((response) => setReview(response.data))
            .catch((requestError) => setError(requestError.response?.data?.message || 'Link de avaliação inválido.'))
            .finally(() => setLoading(false));
    }, [token]);

    const submit = () => {
        if (rating === 0) {
            setError('Escolha uma nota de 1 a 5 estrelas.');
            return;
        }

        setSubmitting(true);
        setError('');
        submitAppointmentReview(token, { rating, comment })
            .then((response) => setReview(response.data))
            .catch((requestError) => setError(requestError.response?.data?.message || 'Não foi possível enviar sua avaliação.'))
            .finally(() => setSubmitting(false));
    };

    if (loading) {
        return (
            <main className="appointment-review-page">
                <div className="appointment-review-state">
                    <Spinner animation="border" />
                    <p>Carregando sua avaliação…</p>
                </div>
            </main>
        );
    }

    // Sem avaliação carregada só resta a mensagem de erro: não há formulário a mostrar.
    if (!review) {
        return (
            <main className="appointment-review-page">
                <section className="appointment-review-shell">
                    <div className="appointment-review-card">
                        <div className="appointment-review-state">
                            <div className="appointment-review-status appointment-review-status--error">
                                <i className="fa-solid fa-triangle-exclamation"></i>
                            </div>
                            <h2>Link indisponível</h2>
                            <p>{error}</p>
                        </div>
                    </div>
                    <PublicBrandSignature className="is-compact" />
                </section>
            </main>
        );
    }

    const scheduledDate = review.scheduledDate ? moment(review.scheduledDate) : null;
    // Depois de respondida, a tela vira recibo: mostra o que foi enviado, sem permitir editar.
    const answered = review.answered;
    const displayRating = answered ? review.rating : (hoveredRating || rating);

    return (
        <main className="appointment-review-page">
            <section className="appointment-review-shell">
                <div className="appointment-review-card">
                    <div className="appointment-review-heading">
                        <div className={`appointment-review-status ${answered ? 'is-done' : ''}`}>
                            <i className={`fa-solid ${answered ? 'fa-circle-check' : 'fa-star'}`}></i>
                        </div>
                        <span className="appointment-review-eyebrow">{review.companyName}</span>
                        <h2>{answered ? 'Obrigado pela sua avaliação!' : 'Como foi seu atendimento?'}</h2>
                        <p>
                            {answered
                                ? 'Sua opinião já foi registrada e ajuda a melhorar o atendimento.'
                                : 'Leva menos de um minuto e ajuda muito a melhorar o atendimento.'}
                        </p>
                    </div>

                    <div className="appointment-review-summary">
                        <div>
                            <span>Profissional</span>
                            <strong>{review.employeeName}</strong>
                        </div>
                        {scheduledDate && (
                            <div>
                                <span>Atendimento</span>
                                <strong>{scheduledDate.format('DD/MM/YYYY [às] HH:mm')}</strong>
                            </div>
                        )}
                    </div>

                    <div className="appointment-review-stars" role="group" aria-label="Nota do atendimento">
                        {STARS.map((star) => (
                            <button
                                key={star}
                                type="button"
                                className={`appointment-review-star ${displayRating >= star ? 'is-active' : ''}`}
                                onClick={() => !answered && setRating(star)}
                                onMouseEnter={() => !answered && setHoveredRating(star)}
                                onMouseLeave={() => !answered && setHoveredRating(0)}
                                disabled={answered}
                                aria-label={`${star} ${star === 1 ? 'estrela' : 'estrelas'}`}
                            >
                                <i className="fa-solid fa-star"></i>
                            </button>
                        ))}
                    </div>

                    <p className="appointment-review-rating-label">
                        {displayRating > 0 ? RATING_LABELS[displayRating] : 'Toque nas estrelas para dar sua nota'}
                    </p>

                    {answered ? (
                        review.comment && (
                            <div className="appointment-review-comment-readonly">
                                <span>Seu comentário</span>
                                <p>{review.comment}</p>
                            </div>
                        )
                    ) : (
                        <>
                            <div className="appointment-review-comment">
                                <label htmlFor="appointment-review-comment">
                                    Quer contar mais alguma coisa? <span>(opcional)</span>
                                </label>
                                <textarea
                                    id="appointment-review-comment"
                                    rows={4}
                                    maxLength={COMMENT_MAX_LENGTH}
                                    value={comment}
                                    onChange={(event) => setComment(event.target.value)}
                                    placeholder="O que foi bom? O que podemos melhorar?"
                                />
                                <small>{comment.length}/{COMMENT_MAX_LENGTH}</small>
                            </div>

                            {error && (
                                <div className="appointment-review-error" role="alert">
                                    <i className="fa-solid fa-circle-exclamation"></i>
                                    {error}
                                </div>
                            )}

                            <Button
                                className="appointment-review-button"
                                onClick={submit}
                                disabled={submitting}
                            >
                                {submitting ? (
                                    <><Spinner animation="border" size="sm" className="me-2" />Enviando…</>
                                ) : (
                                    <><i className="fa-solid fa-paper-plane me-2"></i>Enviar avaliação</>
                                )}
                            </Button>
                        </>
                    )}
                </div>

                <PublicBrandSignature className="is-compact" />
            </section>
        </main>
    );
};

export default AppointmentReviewPage;
